import React, { useState, useEffect, useCallback } from 'react';
import { leavesAPI, holidaysAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Users,
  PartyPopper,
  X,
  Clock,
  Briefcase
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, addMonths, subMonths } from 'date-fns';

const LeaveCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadCalendarData = useCallback(async () => {
    try {
      setIsLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

      const [leavesRes, holidaysRes] = await Promise.all([
        leavesAPI.getLeaveCalendar(startDate, endDate),
        holidaysAPI.getHolidaysForMonth(year, month + 1)
      ]);

      setLeaves(leavesRes.data || []);
      setHolidays(Array.isArray(holidaysRes.data?.data) ? holidaysRes.data.data : []);
    } catch (error) {
      toast.error('Failed to load calendar data');
      setHolidays([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  // Get calendar days with proper week alignment
  const getCalendarDays = () => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    const days = eachDayOfInterval({ start, end });

    // Pad start with empty days
    const startDay = start.getDay();
    const paddedDays = [];
    for (let i = 0; i < startDay; i++) {
      paddedDays.push(null);
    }
    return [...paddedDays, ...days];
  };

  const isHoliday = (date) => {
    if (!date || !Array.isArray(holidays)) return null;
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.find(h => h.date?.split('T')[0] === dateStr);
  };

  const getLeavesForDay = (date) => {
    if (!date) return [];
    return leaves.filter(leave => {
      const start = new Date(leave.startDate);
      const end = new Date(leave.endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      return checkDate >= start && checkDate <= end;
    });
  };

  const handleDayClick = (date) => {
    if (!date) return;
    const dayLeaves = getLeavesForDay(date);
    const holiday = isHoliday(date);

    if (dayLeaves.length > 0 || holiday) {
      setSelectedDayData({
        date,
        leaves: dayLeaves,
        holiday: holiday?.name || null
      });
      setShowDetailsModal(true);
    }
  };

  const calendarDays = getCalendarDays();
  const weekDays = [
    { short: 'S', long: 'Sun' },
    { short: 'M', long: 'Mon' },
    { short: 'T', long: 'Tue' },
    { short: 'W', long: 'Wed' },
    { short: 'T', long: 'Thu' },
    { short: 'F', long: 'Fri' },
    { short: 'S', long: 'Sat' },
  ];

  // Get upcoming leaves for sidebar
  const upcomingLeaves = leaves
    .filter(l => new Date(l.startDate) >= new Date())
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    .slice(0, 5);

  // Get holidays for this month for sidebar
  const monthHolidays = holidays.slice(0, 4);

  return (
    <div className="">
      <div className="">

        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-gray-500" />
                Time-Off Calendar
              </h1>
              <p className="text-sm text-slate-500 mt-1">Track team availability, leaves & holidays</p>
            </div>

            {/* Quick Stats */}
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:gap-3">
              <div className="flex items-center gap-2 px-3 py-2 sm:px-4 bg-white rounded-md border border-slate-200 shadow-sm">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                  <Briefcase size={16} className="text-gray-700" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-gray-900">{leaves.length}</div>
                  <div className="text-[10px] text-slate-500 uppercase font-medium">Leaves</div>
                </div>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 sm:px-4 bg-white rounded-md border border-slate-200 shadow-sm">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                  <PartyPopper size={16} className="text-gray-700" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-gray-900">{holidays.length}</div>
                  <div className="text-[10px] text-slate-500 uppercase font-medium">Holidays</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid: Calendar + Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

          {/* Calendar Section */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">

              {/* Month Navigation */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 text-gray-900">
                <button
                  onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                  className="p-2 hover:bg-white/20 rounded-md transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>

                <div className="text-center">
                  <h2 className="text-base font-semibold">{format(currentDate, 'MMMM yyyy')}</h2>
                  <p className="text-xs opacity-80 mt-0.5">Click on a day to view details</p>
                </div>

                <button
                  onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                  className="p-2 hover:bg-white/20 rounded-md transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="p-2 sm:p-4">
                {/* Weekday Headers */}
                <div className="grid grid-cols-7 mb-2">
                  {weekDays.map((day, i) => (
                    <div key={i} className={`text-center text-[10px] sm:text-xs font-bold uppercase tracking-wide py-1.5 sm:py-2 ${i === 0 ? 'text-gray-700' : 'text-slate-400'}`}>
                      <span className="sm:hidden">{day.short}</span>
                      <span className="hidden sm:inline">{day.long}</span>
                    </div>
                  ))}
                </div>

                {/* Days Grid */}
                {isLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-200"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((date, index) => {
                      if (!date) {
                        return <div key={`empty-${index}`} className="aspect-square p-1"></div>;
                      }

                      const dayLeaves = getLeavesForDay(date);
                      const holiday = isHoliday(date);
                      const today = isToday(date);
                      const isSunday = date.getDay() === 0;

                      return (
                        <motion.div
                          key={format(date, 'yyyy-MM-dd')}
                          whileHover={{ scale: 1.02 }}
                          onClick={() => handleDayClick(date)}
                          className={`
                            aspect-square p-1 sm:p-1.5 rounded-md cursor-pointer transition-all relative min-w-0
                            ${today ? 'bg-gray-900 text-white shadow-none' :
                              holiday ? 'bg-gray-100 border border-gray-200 sm:border-2' :
                                dayLeaves.length > 0 ? 'bg-gray-100 border border-gray-200' :
                                  'bg-slate-50 hover:bg-slate-100 border border-transparent hover:border-slate-200'}
                          `}
                        >
                          {/* Day Number */}
                          <div className={`text-[11px] sm:text-sm font-bold ${today ? 'text-white' :
                              holiday ? 'text-gray-700' :
                                isSunday ? 'text-gray-700' :
                                  'text-slate-700'
                            }`}>
                            {format(date, 'd')}
                          </div>

                          {/* Holiday Indicator */}
                          {holiday && !today && (
                            <div className="absolute top-1 right-1">
                              <PartyPopper size={10} className="text-gray-700" />
                            </div>
                          )}

                          {/* Leave Indicators */}
                          {dayLeaves.length > 0 && (
                            <div className="absolute bottom-1 left-1 right-1">
                              <div className="flex gap-0.5 flex-wrap">
                                {dayLeaves.slice(0, 3).map((leave, i) => (
                                  <div
                                    key={i}
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ backgroundColor: leave.leaveTypeId?.color || '#6366f1' }}
                                    title={leave.employeeId?.name}
                                  />
                                ))}
                                {dayLeaves.length > 3 && (
                                  <span className="text-[8px] font-bold text-slate-500">+{dayLeaves.length - 3}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Legend */}
              <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex flex-wrap gap-4 text-xs">
                <span className="text-slate-400 font-bold uppercase">Legend:</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-gray-900"></div>
                  <span className="text-slate-600">Today</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-gray-100 border border-gray-200"></div>
                  <span className="text-slate-600">Holiday</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-gray-100 border border-gray-200"></div>
                  <span className="text-slate-600">Leave</span>
                </div>
                {/* Dynamic leave type colors */}
                {leaves.reduce((acc, leave) => {
                  if (leave.leaveTypeId && !acc.find(l => l.leaveTypeId?._id === leave.leaveTypeId?._id)) {
                    acc.push(leave);
                  }
                  return acc;
                }, []).slice(0, 4).map(leave => (
                  <div key={leave.leaveTypeId?._id} className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: leave.leaveTypeId?.color }}></div>
                    <span className="text-slate-500">{leave.leaveTypeId?.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">

            {/* Upcoming Leaves */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Clock size={14} className="text-gray-700" />
                Upcoming Leaves
              </h3>

              {upcomingLeaves.length > 0 ? (
                <div className="space-y-2">
                  {upcomingLeaves.map(leave => (
                    <div key={leave._id} className="p-3 bg-slate-50 rounded-md border border-slate-100 hover:border-slate-200 transition-colors">
                      <div className="flex items-start gap-2">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white shrink-0"
                          style={{ backgroundColor: leave.leaveTypeId?.color || '#6366f1' }}
                        >
                          {leave.employeeId?.name?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-700 truncate">{leave.employeeId?.name}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {format(new Date(leave.startDate), 'MMM d')} - {format(new Date(leave.endDate), 'MMM d')}
                          </div>
                          <div
                            className="text-[10px] font-medium mt-1 inline-block px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: leave.leaveTypeId?.color + '20', color: leave.leaveTypeId?.color }}
                          >
                            {leave.leaveTypeId?.name}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-400 text-sm">
                  No upcoming leaves
                </div>
              )}
            </div>

            {/* Holidays This Month */}
            <div className="bg-gray-900 rounded-lg shadow-lg p-4 text-white relative overflow-hidden">
              <div className="absolute -top-6 -right-6 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>

              <h3 className="text-sm font-bold uppercase tracking-wide mb-3 flex items-center gap-2 opacity-90">
                <PartyPopper size={14} />
                Holidays This Month
              </h3>

              {monthHolidays.length > 0 ? (
                <div className="space-y-2 relative z-10">
                  {monthHolidays.map((holiday, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm p-2.5 rounded-md border border-white/10">
                      <div className="bg-white text-gray-700 rounded-lg px-2 py-1 text-center min-w-[40px]">
                        <div className="text-[9px] font-bold uppercase">{format(new Date(holiday.date), 'MMM')}</div>
                        <div className="text-base font-semibold leading-none">{format(new Date(holiday.date), 'd')}</div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold leading-tight">{holiday.name}</div>
                        <div className="text-[10px] opacity-75">{format(new Date(holiday.date), 'EEEE')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 opacity-70 text-sm relative z-10">
                  No holidays this month
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Day Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedDayData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-md rounded-lg shadow-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b border-gray-200 text-gray-900">
                <div>
                  <h3 className="text-sm font-semibold">{format(selectedDayData.date, 'EEEE')}</h3>
                  <p className="text-sm opacity-80">{format(selectedDayData.date, 'MMMM d, yyyy')}</p>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-5 max-h-[60vh] overflow-y-auto">
                {/* Holiday Section */}
                {selectedDayData.holiday && (
                  <div className="mb-4 p-4 bg-gray-100 rounded-md border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        <PartyPopper className="w-5 h-5 text-gray-700" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-gray-700 uppercase">Holiday</div>
                        <div className="text-base font-semibold text-slate-800">{selectedDayData.holiday}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Leaves Section */}
                {selectedDayData.leaves.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Users size={16} className="text-gray-700" />
                      <h4 className="text-sm font-bold text-slate-700">
                        Employees on Leave ({selectedDayData.leaves.length})
                      </h4>
                    </div>

                    <div className="space-y-2">
                      {selectedDayData.leaves.map(leave => (
                        <div key={leave._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-slate-100">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: leave.leaveTypeId?.color || '#6366f1' }}
                            >
                              {leave.employeeId?.name?.[0]}
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-slate-800">{leave.employeeId?.name}</div>
                              <div className="text-[10px] text-slate-500">{leave.employeeId?.department}</div>
                            </div>
                          </div>
                          <div
                            className="text-xs font-bold px-2 py-1 rounded-lg"
                            style={{ backgroundColor: leave.leaveTypeId?.color + '20', color: leave.leaveTypeId?.color }}
                          >
                            {leave.leaveTypeId?.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {!selectedDayData.holiday && selectedDayData.leaves.length === 0 && (
                  <div className="text-center py-10">
                    <CalendarIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-400 text-sm">No events for this day</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeaveCalendar;